import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

import { ETHICO_ENV_CONFIG } from '../../app.config';
import { ETHICO_MYCM_CONFIG_KEY } from '../../app.constants';
import { MyCMConfig, UserInfo } from '../models';
import { debugLog, debugWarn } from '../utils/debug-logger';

/**
 * The role of this service is applicable only if the template builder is integrated with MyCM via iFrame.
 * Service to handle MyCM integration.
 * This service communicates with the MyCM application using postMessage API.
 */
@Injectable({
  providedIn: 'root',
})
export class MyCMIntegrationService {
  myCMConfig$ = new BehaviorSubject<MyCMConfig | null>(null);
  environmentConfig = inject(ETHICO_ENV_CONFIG);
  /** Resolved by handleMessage (or by the 5 s safety-net timeout) to unblock APP_INITIALIZER. */
  private _configReadyResolve?: () => void;
  private _configReadyTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _configReadySettled = false;
  
  hasValidConfigurations(): boolean {
    return !!this.myCMConfig$.value?.apiUrl;
  }

  get activeUserInfo(): UserInfo | undefined {
    return this.myCMConfig$.value?.userInfo;
  }

  // This method initializes the message listener for the MyCM integration.
  // Returns a Promise so Angular's APP_INITIALIZER waits for myCM config before
  // the router starts running guards — preventing the /not-found race condition.
  initializeMessageListener(): Promise<void> {
    if (window.self !== window.top) {//nosonar
      this._configReadySettled = false;
      debugLog('MyCMIntegration', 'initializeMessageListener start', {
        href: globalThis.location.href,
        referrer: document.referrer,
        configuredMyCMAppUrl: this.environmentConfig.myCMAppUrl,
      });
      // Promise resolved by handleMessage when config arrives, or after 5 s as a safety net.
      // Returning it here causes Angular to hold off routing until we have the customer ID.
      const configReady = new Promise<void>((resolve) => {
        this._configReadyResolve = () => {
          resolve();
        };
        this._configReadyTimeoutId = setTimeout(() => {
          debugWarn('MyCMIntegration', 'configReady timeout reached (15000ms), proceeding without guaranteed config');
          this.resolveConfigReady();
        }, 15000);
      });

      window.addEventListener('message', (event) => {
        if (event.data?.type === ETHICO_MYCM_CONFIG_KEY || event.data?.token || event.data?.jwtToken) {
          debugLog('MyCMIntegration', 'message event captured', {
            origin: event.origin,
            type: event.data?.type,
            hasToken: !!(event.data?.token || event.data?.jwtToken || event.data?.payload?.token),
          });
          this.handleMessage(event);
        }
      });

      const mouseMove$ = fromEvent(globalThis, 'mousemove');
      // notify MyCM app that the template builder is active every 10 seconds
      // this happens if the user is not interacting with the template builder app
      const throttledMouseMove$ = mouseMove$.pipe(throttleTime(10000));
      throttledMouseMove$.subscribe(() => {
        this.postMessage(`${ETHICO_MYCM_CONFIG_KEY}-active`);
      });

      // If MyCM sent the token before Angular bootstrapped, replay it now (synchronously,
      // before the router runs its first navigation / guards).
      const earlyWindow = window as Window & { __earlyMyCMEvent?: MessageEvent };//nosonar
      const early = earlyWindow.__earlyMyCMEvent;
      if (early) {
        debugLog('MyCMIntegration', 'replaying early captured message', {
          origin: early.origin,
          type: early.data?.type,
          hasToken: !!(early.data?.token || early.data?.jwtToken || early.data?.payload?.token),
        });
        this.handleMessage(early);
        delete earlyWindow.__earlyMyCMEvent;
        if (!this.myCMConfig$.getValue()?.token && !this.myCMConfig$.getValue()?.apiUrl) {
          debugLog('MyCMIntegration', 'early message did not contain config/token, sending ready signal');
          this.postMessage(`${ETHICO_MYCM_CONFIG_KEY}-ready`);
        }
      } else {
        // Token wasn't captured early — send a ready signal so MyCM re-sends the config.
        debugLog('MyCMIntegration', 'no early message found, sending ready signal');
        this.postMessage(`${ETHICO_MYCM_CONFIG_KEY}-ready`);
      }

      return configReady;
    } else {
      this.myCMConfig$.next({
        apiUrl: '',
        customerId: '',
        userInfo: undefined,
        token: '',
      } as any);
      return Promise.resolve();
    }
  }

  postMessage(message: string): void {
    if (!window.top) return;
    const targetOrigin = this.environmentConfig.myCMAppUrl;
    if (!targetOrigin) {
      debugWarn('MyCMIntegration', 'postMessage skipped: myCMAppUrl not configured');
      return;
    }
    window.top.postMessage(
      {
        type: `${ETHICO_MYCM_CONFIG_KEY}`,
        payload: message,
      },
      targetOrigin,
    );
  }

  // notifyMyCmParentApp({
  //   notificationType,
  //   data,
  // }: {
  //   notificationType: MyCMParentNotificationType;
  //   data: any;
  // }) {
  //   if (window.top) {
  //     window.top.postMessage(
  //       {
  //         type: `${ETHICO_MYCM_CONFIG_KEY}-notification` as MyCMParentNotificationType,
  //         payload: notificationType,
  //         data,
  //       },
  //       this.environmentConfig.myCMAppUrl,
  //     );
  //   }
  // }

  getBearerToken(): string {
    const detail = this.myCMConfig$.getValue();
    // Top-level token is the JWT used for API calls.
    // userInfo.token is a fallback for legacy payloads.
    return detail?.token || detail?.userInfo?.token || '';
  }

  private normalizeOrigin(url: string): string {
    // Normalize URL by removing www prefix for comparison
    return url?.replace(/^(https?:\/\/)www\./, '$1') || '';
  }

  handleMessage(event: MessageEvent): void {
    const configuredUrl = this.environmentConfig.myCMAppUrl;
    debugLog('MyCMIntegration', 'handleMessage begin', {
      origin: event.origin,
      type: event.data?.type,
      hasPayloadObject: !!event.data?.payload && typeof event.data?.payload === 'object',
      hasTokenTopLevel: !!(event.data?.token || event.data?.jwtToken),
      configuredUrl,
    });

    // Hard-reject messages from untrusted origins. Fail closed when config is missing.
    if (!configuredUrl) {
      debugLog('MyCMIntegration', 'handleMessage rejected: no configured myCMAppUrl yet');
      return;
    }
    let trustedOrigin: string;
    try {
      trustedOrigin = new URL(configuredUrl).origin;
    } catch {
      return;
    }
    const normalizedTrusted = this.normalizeOrigin(trustedOrigin);
    const normalizedReceived = this.normalizeOrigin(event.origin);
    if (event.origin !== trustedOrigin && normalizedReceived !== normalizedTrusted) {
      debugLog('MyCMIntegration', 'handleMessage rejected: origin mismatch', {
        receivedOrigin: event.origin,
        trustedOrigin,
        normalizedReceived,
        normalizedTrusted,
      });
      return;
    }

    // Hand-shake with MyCM parent app.
    if (!this.myCMConfig$.getValue()) {
      this.postMessage(`${ETHICO_MYCM_CONFIG_KEY}-ready`);
    }

    // Detect message format:
    // Format A: { type: 'ethico-myCM-config', payload: { apiUrl, customerId, token, … } }
    // Format B: { token, jwtToken, url } — sent by DrEmployeePortalWrapperComponent
    if (event.data?.payload && typeof event.data.payload === 'object') {
      debugLog('MyCMIntegration', 'accepted format A payload');
      this.myCMConfig$.next(event.data.payload);
    } else if (event.data?.token || event.data?.jwtToken) {
      debugLog('MyCMIntegration', 'accepted format B payload');
      const token = event.data.token ?? event.data.jwtToken;
      const existing = this.myCMConfig$.getValue();
      this.myCMConfig$.next({
        apiUrl: event.data.url ?? existing?.apiUrl ?? '',
        customerId: existing?.customerId ?? '',
        token,
        disclosureId: existing?.disclosureId,
        readOnly: existing?.readOnly,
        viewOnly: existing?.viewOnly,
        userInfo: existing?.userInfo,
        error: existing?.error,
      } as MyCMConfig);
    } else {
      debugLog('MyCMIntegration', 'handleMessage ignored: unsupported message shape');
      return;
    }
    debugLog('MyCMIntegration', 'myCMConfig updated', {
      hasToken: !!this.myCMConfig$.value?.token,
      customerId: this.myCMConfig$.value?.customerId,
      apiUrl: this.myCMConfig$.value?.apiUrl,
    });
    this.storeCustomerIdFromConfig();
    this.resolveConfigReady();
  }

  private resolveConfigReady(): void {
    if (this._configReadySettled) {
      return;
    }
    this._configReadySettled = true;
    if (this._configReadyTimeoutId) {
      clearTimeout(this._configReadyTimeoutId);
      this._configReadyTimeoutId = null;
    }
    this._configReadyResolve?.();
    this._configReadyResolve = undefined;
  }

  /** Writes the resolved customer ID to localStorage so the bootstrap guard can resolve it. */
  private storeCustomerIdFromConfig(): void {
    const customerId = this.myCMConfig$.value?.customerId;
    if (customerId) {
      localStorage.setItem('customerId', customerId);
    }
  }
}
